import type { InlineMarks, MarkupRun, MarkupSpanRun } from './parse-inline-markers';

/**
 * Canonical letter order, so the same span always serializes the same way and
 * a source-mode author sees `{bi1|…}` rather than whichever order an editor
 * happened to apply the marks in.
 */
const MARK_LETTERS: ReadonlyArray<readonly [keyof InlineMarks, string]> = [
  ['bold', 'b'],
  ['italic', 'i'],
  ['strike', 's'],
];

const markLetters = (marks: InlineMarks): string =>
  MARK_LETTERS.filter(([mark]) => marks[mark])
    .map(([, letter]) => letter)
    .join('');

/**
 * A span back to its marker. Text that carries no marks, no link and no styling
 * is emitted bare: a marker with nothing to say would only be noise an author
 * has to read past, and it round-trips to the same runs either way.
 */
const formatSpan = (run: MarkupSpanRun): string => {
  const letters = markLetters(run.marks);
  const index = run.index === undefined ? '' : `${run.index}`;
  const styleIndex = run.styleIndex === undefined ? '' : `.${run.styleIndex}`;

  if (letters === '' && index === '' && styleIndex === '') {
    return run.text;
  }
  return `{${letters}${index}${styleIndex}|${run.text}}`;
};

/** One run as authored text. */
export function formatInlineMarker(run: MarkupRun): string {
  switch (run.kind) {
    case 'text':
      return run.text;
    case 'break':
      return '{br|}';
    default:
      return formatSpan(run);
  }
}

/**
 * Runs back to the string they were parsed from.
 *
 * This is the writer half of the grammar, and it lives beside the parser so
 * that an editor never hand-assembles marker syntax. `parseInlineMarkers` and
 * this function are inverses for anything the parser produces, which is what
 * lets a rich editor round-trip through source mode without rewriting content
 * the author did not touch.
 */
export function serializeInlineMarkers(runs: readonly MarkupRun[]): string {
  return runs.map(formatInlineMarker).join('');
}
